import { useState, useEffect, useCallback, useRef } from 'react'
import reportService from '@api/v1/reportService'
import LoadingSpinner from '@components/common/LoadingSpinner'
import { buildPayload } from './aiPayload'

/**
 * The AI panel for a report. Two modes, chosen by the per-user "AI Assistant"
 * setting (Settings → AI Assistant, persisted per user/tenant):
 *   - `summary` — a click-to-run "✨ AI summary" (the original behavior).
 *   - `chat`    — a bounded Q&A: it opens with a summary turn, offers up to 3
 *                 suggested follow-ups each turn, AND takes a typed question.
 *
 * ~~There is no free-text box — every turn is one deliberate click = one grounded
 * AI call, so there is no open-ended abuse surface.~~ **REVERSED 2026-08-21, on
 * Jordan's call.** The trigger says "Ask" and then gave you nothing to ask with,
 * which he reasonably rejected. The bound that mattered was never the absence of
 * the box: `cb-api/src/domains/reports/routes.js:611-615` already filtered roles,
 * dropped non-string content, truncated every message to 600 chars, kept the last
 * 10 turns and required the final turn to be a user one — so the box exposes a
 * surface the server had already bounded. Kept from the original decision: ONE AI
 * call per submit, and the report's own figures rebuilt and sent on every turn.
 *
 * Both reuse the grid's export-view getter (the same mechanism as "export what
 * you see") so the model works from exactly what the user sees — visible columns,
 * filtered rows, declared SUM totals. Data is sent to cb-api's Workers AI
 * endpoint only, never to a third-party API.
 *
 * The payload itself is built by `aiPayload.js`, which rounds each numeric cell
 * to its own column's DISPLAYED precision (CC-53148). The export view carries
 * CB's unrounded `dataValue` so an .xlsx stays summable — but quoting that back
 * verbatim had the AI report "4608.09688598 billable hours" under a grid reading
 * "4,608.1". Exports are unchanged; only the model's copy is rounded.
 */

const DISCLAIMER =
  'AI-generated from the report’s totals and a sample of rows — verify against the data before relying on it.'

/*
 * The panel's shell, matching the Figma (frame 110:11804).
 *
 * Ours was a plain white card with a bold heading and an × — Jordan: *"the AI
 * Summary box doesn't look like the mocks at all."* The mock draws a VIOLET-tinted
 * card with an "AI" badge, a scope line, an uppercase section label, pill-shaped
 * suggestions and a footnote disclaimer.
 *
 * The colours are not sampled from the mock: `rc-ai-bg` / `rc-ai-line` /
 * `rc-ai-text` already existed, created from the designer's own component doc
 * (75:3714) with measured contrast, and purple is FENCED to the AI surface by
 * their rule. The trigger button already used them; the panel never did. So this
 * is applying a token set that was sitting right there rather than inventing one.
 */
const AiPanelShell = ({ title, scope, onRegenerate, regenerateLabel, onDismiss, dismissLabel, busy, children }) => (
  <div className="mb-3">
    <div
      role="region"
      aria-label={title}
      aria-busy={busy}
      className="rounded-rc-card border border-rc-ai-line bg-rc-ai-bg px-4 py-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <span aria-hidden="true" className="text-rc-ai-text">✨</span>
          <h3 className="text-sm font-semibold text-rc-ink">{title}</h3>
          {/* The mock's small "AI" badge — it labels the surface as generated
              content, which in a billing app is worth stating twice (the
              footnote below is the other). */}
          <span className="rounded-full bg-white/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rc-ai-text">
            AI
          </span>
        </div>
        <div className="flex flex-none items-center gap-3">
          {onRegenerate && (
            <button type="button" onClick={onRegenerate} disabled={busy} className="btn-cb-tertiary disabled:opacity-50">
              {regenerateLabel}
            </button>
          )}
          <button type="button" onClick={onDismiss} className="btn-cb-tertiary" aria-label={dismissLabel}>
            Hide
          </button>
        </div>
      </div>

      {/* Scope line. In the mock this reads "Scope: as of 7/10/2026 · all
          attorneys · 40 rows". It is the honest counterpart to the disclaimer:
          the model saw THIS filtered view, not the whole ledger, and on a
          financial report the difference matters. */}
      {scope && <p className="mt-0.5 text-[11px] text-rc-muted">{scope}</p>}

      <div className="mt-2">{children}</div>

      <p className="mt-3 text-[11px] text-rc-muted">{DISCLAIMER}</p>
    </div>
  </div>
)

/**
 * Run `start` as soon as the report's data is actually available.
 *
 * The generic grid publishes its export view in its OWN effect, which runs AFTER
 * this panel's on first mount, so `getView()` is null for a beat. Rather than
 * flash "Run the report first", hold and retry briefly (~2s of grace).
 *
 * Extracted so BOTH modes share it. Chat mode has always auto-opened this way;
 * summary mode used to make the user click a second button, and when that button
 * was removed this became the only thing standing between "the panel opened" and
 * "the panel says run the report first".
 */
const useAutoStart = ({ getView, data, reportName, start, onGiveUp }) => {
  const startedRef = useRef(false)
  useEffect(() => {
    if (startedRef.current) return
    let cancelled = false
    let attempts = 0
    const tryStart = () => {
      if (cancelled || startedRef.current) return
      let ready = false
      try { buildPayload({ getView, data, reportName }); ready = true } catch { /* view not published yet */ }
      if (ready) {
        startedRef.current = true
        start()
      } else if (attempts++ < 25) {
        setTimeout(tryStart, 80) // ~2s of grace while the grid publishes its view
      } else {
        onGiveUp()
      }
    }
    tryStart()
    return () => { cancelled = true }
  }, [getView, data, reportName, start, onGiveUp])
}

// ---------------------------------------------------------------------------
// Summary mode — runs as soon as it is opened.
// ---------------------------------------------------------------------------
/*
 * This panel used to open at `status: 'idle'` and render its OWN "✨ AI summary"
 * button. Since the host only mounts it once the user has already clicked
 * "AI Summary" up in the action bar, that made the top button do nothing except
 * reveal a second button — Jordan's words: *"clicking AI Summary just exposes
 * another AI summary button — that is stupid."* He is right: the top button IS
 * the trigger, so opening the panel now runs it.
 *
 * The self-trigger is gone. Re-running is still possible (Regenerate), and the
 * close button now collapses the whole panel via `onDismiss` rather than
 * returning to an idle state — returning to idle is what would put the redundant
 * button back on screen.
 */
const SummaryPanel = ({ getView, data, reportName, onDismiss, scope }) => {
  const [status, setStatus] = useState('loading') // loading | done | error
  const [summary, setSummary] = useState('')
  const [error, setError] = useState('')

  const summarize = useCallback(async () => {
    setStatus('loading')
    setError('')
    setSummary('')
    try {
      const payload = buildPayload({ getView, data, reportName })
      const text = await reportService.aiSummary(payload)
      if (!text) throw new Error('No summary was returned.')
      setSummary(text)
      setStatus('done')
    } catch (err) {
      setError(err?.message || 'Summary failed. Try again.')
      setStatus('error')
    }
  }, [getView, data, reportName])

  const giveUp = useCallback(() => {
    setStatus('error')
    setError('Run the report first, then summarize it.')
  }, [])

  useAutoStart({ getView, data, reportName, start: summarize, onGiveUp: giveUp })

  return (
    <AiPanelShell
      title="AI summary"
      scope={scope}
      onRegenerate={summarize}
      regenerateLabel={status === 'error' ? 'Try again' : 'Regenerate'}
      onDismiss={onDismiss}
      dismissLabel="Close the AI summary"
      busy={status === 'loading'}
    >
      {status === 'loading' && <LoadingSpinner message="Summarizing this report…" />}
      {status === 'error' && <p className="text-sm text-red-700">{error}</p>}
      {status === 'done' && (
        <p className="whitespace-pre-line text-sm text-rc-ink-2">{summary}</p>
      )}
    </AiPanelShell>
  )
}

// ---------------------------------------------------------------------------
// Chat mode — bounded, suggested-question-driven. Opens with a summary turn.
// ---------------------------------------------------------------------------
const SUMMARY_PROMPT = 'Give a brief summary of this report.'

const ChatPanel = ({ getView, data, reportName, onDismiss, scope }) => {
  const [messages, setMessages] = useState([]) // [{ role:'user'|'assistant', content }]
  const [suggestions, setSuggestions] = useState([])
  const [status, setStatus] = useState('loading') // loading | done | error
  const [error, setError] = useState('')
  const [draft, setDraft] = useState('') // the free-text question being typed

  // One AI call per user turn. `question` is appended to `prior` and sent; the
  // report data is rebuilt each turn so it reflects the current view/filters.
  const ask = useCallback(async (question, prior) => {
    setStatus('loading')
    setError('')
    setSuggestions([])
    const convo = [...prior, { role: 'user', content: question }]
    setMessages(convo)
    try {
      const payload = buildPayload({ getView, data, reportName })
      const { answer, suggestions: next } = await reportService.aiChat({ ...payload, messages: convo })
      if (!answer) throw new Error('No answer was returned.')
      setMessages([...convo, { role: 'assistant', content: answer }])
      setSuggestions(Array.isArray(next) ? next : [])
      setStatus('done')
    } catch (err) {
      setError(err?.message || 'The assistant is unavailable right now. Try again.')
      setStatus('error')
    }
  }, [getView, data, reportName])

  // Auto-open with a summary turn, once the report view/data is actually ready.
  // Same helper the summary panel uses — this logic was here first and is now
  // shared rather than duplicated.
  const startChat = useCallback(() => ask(SUMMARY_PROMPT, []), [ask])
  const giveUp = useCallback(() => {
    setStatus('error')
    setError('Run the report first, then ask about it.')
  }, [])
  useAutoStart({ getView, data, reportName, start: startChat, onGiveUp: giveUp })

  // Hide the implicit summary kickoff — the user didn't type it.
  const visible = messages.filter((m, i) => !(i === 0 && m.role === 'user' && m.content === SUMMARY_PROMPT))
  const lastFailedQuestion = status === 'error' && messages.length && messages[messages.length - 1].role === 'user'
    ? messages[messages.length - 1].content
    : ''

  return (
    <AiPanelShell
      title="Ask about this report"
      scope={scope}
      onRegenerate={() => ask(SUMMARY_PROMPT, [])}
      regenerateLabel="Start over"
      onDismiss={onDismiss}
      dismissLabel="Close the AI panel"
      busy={status === 'loading'}
    >
      {/* Conversation */}
      <div role="log" aria-live="polite" className="space-y-3">
        {visible.map((m, i) =>
          m.role === 'user' ? (
            <p key={i} className="text-sm font-medium text-cb-navy">
              <span className="text-gray-500">You asked: </span>
              {m.content}
            </p>
          ) : (
            <p key={i} className="text-sm text-gray-700 whitespace-pre-line">
              {m.content}
            </p>
          )
        )}
        {status === 'loading' && (
          <LoadingSpinner message={visible.length ? 'Thinking…' : 'Reading this report…'} />
        )}
        {status === 'error' && (
          <div className="text-sm text-red-700">
            <p>{error}</p>
            {lastFailedQuestion && (
              <button
                type="button"
                onClick={() => ask(lastFailedQuestion, messages.slice(0, -1))}
                className="mt-1 text-[11px] font-medium text-cb-accent-text hover:underline"
              >
                Try again
              </button>
            )}
          </div>
        )}
      </div>

      {/* Suggested follow-up questions — the only way to advance (bounded). */}
      {status === 'done' && suggestions.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-rc-muted">Suggested questions</p>
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map((q, i) => (
              <button
                key={i}
                type="button"
                onClick={() => ask(q, messages)}
                className="inline-flex items-center rounded-full border border-rc-ai-line bg-white px-2.5 py-1 text-xs text-rc-ai-text transition-colors hover:bg-white/60"
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      {/*
        The free-text box, from the mock (frame 110:11804).

        This REVERSES a previously documented decision, deliberately and on
        Jordan's call. CLAUDE.md used to say chat mode has "no free-text box —
        every turn is one deliberate click = one grounded AI call, so there is no
        open-ended abuse surface." I flagged the omission on CC-53140 rather than
        building it unasked; Jordan's answer was that the trigger says "Ask" and
        then gives you nothing to ask with, which is a fair objection — a label
        that promises something the UI does not deliver.

        What the original decision was protecting, and what still protects it:
        the endpoint was already hardened for arbitrary input BEFORE this box
        existed (`cb-api/src/domains/reports/routes.js:611-615`) — it filters
        roles, drops non-string content, truncates every message to 600
        characters, keeps only the last 10 turns, and rejects a conversation
        whose last turn is not a user one. So the box does not open an unbounded
        surface; it exposes one the server already bounded.

        Grounding is UNCHANGED and stays the hard rule: the payload is still
        built from the report's own view on every turn, and the user's text is
        only ever the question. `maxLength` matches the server's own 600-char cap
        so the UI cannot silently send more than the server will keep.

        A <form> rather than an input + click handler, so Enter submits natively
        and it is keyboard-operable without our own key handling (WCAG 2.1.1).
      */}
      <form
        className="mt-3 flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          const q = draft.trim()
          if (!q || status === 'loading') return
          setDraft('')
          ask(q, messages)
        }}
      >
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={status === 'loading'}
          maxLength={600}
          /* A DISTINCT accessible name from the panel region, which is already
             "Ask about this report". Two elements sharing one accessible name is
             ambiguous for a screen reader (and made getByLabelText match both). */
          aria-label="Ask a question about this report"
          placeholder="Ask about this report…"
          className="min-w-0 flex-1 rounded-full border border-rc-ai-line bg-white px-3.5 py-1.5 text-sm text-rc-ink placeholder:text-rc-muted focus:outline-none focus:ring-2 focus:ring-rc-ai-line/50 disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={!draft.trim() || status === 'loading'}
          aria-label="Send question"
          className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-rc-ai-text text-white transition-opacity hover:opacity-90 disabled:opacity-40"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.769 59.769 0 0121.485 12 59.768 59.768 0 013.27 20.876L5.999 12zm0 0h7.5" />
          </svg>
        </button>
      </form>
    </AiPanelShell>
  )
}

const ReportAiSummary = ({ getView, data, reportName, mode = 'summary', onDismiss, scope }) => {
  if (mode === 'chat') return <ChatPanel getView={getView} data={data} reportName={reportName} onDismiss={onDismiss} scope={scope} />
  return <SummaryPanel getView={getView} data={data} reportName={reportName} onDismiss={onDismiss} scope={scope} />
}

export default ReportAiSummary
